import { NO, YES } from "uicore-ts"
import { RHCore } from "../Core/RHCore"
import type { RHProcurementInformationResult } from "../Core/RHProcurementStore"
import type { RHReconciliationRoundSummary } from "../SocketClientFunctions"
import { RHProcurementWinnerStatus } from "../Custom components/RH/Utils_slop/RHProcurementWinnerStatus"
import { referenceId } from "../Core/RHReference"


export interface ProcurementRound {
    procurementId: string
    partId: string
    rootPartId: string
    roundNumber: number
    information: RHProcurementInformationResult
}


export class ProcurementRoundContext {
    readonly informationByProcurementId = new Map<string, RHProcurementInformationResult>()
    readonly roundsByPartId = new Map<string, ProcurementRound[]>()
    readonly selectedProcurementIdsByPartId = new Map<string, string>()
    rootInformation?: RHProcurementInformationResult
    focusedPartId?: string
    _loadGeneration = 0

    get hasRounds(): boolean {
        return [...this.roundsByPartId.values()].some(rounds => rounds.length > 1)
    }

    get roundsWithViewableContracts(): ProcurementRound[] {
        return [...this.roundsByPartId.values()].flat().filter(round => {
            const information = round.information
            const procurement = information.procurement
            const winningOfferId = procurement.partWinnerOfferIds?.[round.partId]
            if (!winningOfferId || !RHProcurementWinnerStatus.hasFinalizedWinningPart({
                ...procurement,
                partWinnerOfferIds: { [round.partId]: winningOfferId }
            })) {
                return NO
            }
            if (information.isCurrentUserOnProcuringSide) {
                return information.capabilities?.canViewDetailed === YES
            }
            return procurement.isCurrentUserCompanyRegisteredAsTenderer === YES &&
                (information.offerCapabilities?.teamRole === "responsible" ||
                    information.offerCapabilities?.teamRole === "authorized")
        })
    }

    async loadForProcurement(
        rootInformation: RHProcurementInformationResult,
        visibleChain: RHReconciliationRoundSummary[],
        isLoadCurrent: () => boolean,
        requestedProcurementId?: string,
        communicationProcurementIds: string[] = []
    ): Promise<boolean> {
        const generation = ++this._loadGeneration
        const rootId = rootInformation.procurement._id?.toString()
        if (!rootId) {
            return NO
        }
        const informationById = new Map<string, RHProcurementInformationResult>([[rootId, rootInformation]])
        const pendingIds = new Set(visibleChain.map(round => round.id))
        communicationProcurementIds.forEach(procurementId => pendingIds.add(procurementId))
        const visitedIds = new Set<string>()
        pendingIds.add(rootId)
        if (requestedProcurementId) {
            pendingIds.add(requestedProcurementId)
        }

        while (pendingIds.size > 0) {
            const procurementId = pendingIds.values().next().value as string
            pendingIds.delete(procurementId)
            if (visitedIds.has(procurementId)) {
                continue
            }
            visitedIds.add(procurementId)
            let information = informationById.get(procurementId)
            if (!information) {
                information = await RHCore.sharedInstance.procurementStore.retrieveProcurementInformation(procurementId)
                if (generation !== this._loadGeneration || !isLoadCurrent()) {
                    return NO
                }
                informationById.set(procurementId, information)
            }
            const procurement = information.procurement
            const parentId = referenceId(procurement.reconciliationParentProcurement)
            if (parentId) {
                pendingIds.add(parentId)
            }
            // Private links are available only in the procurer's authorized DTO.
            if (information.isCurrentUserOnProcuringSide && information.capabilities?.canViewDetailed === YES) {
                for (const childReferences of Object.values(procurement.reconciliationChildProcurementIds ?? {})) {
                    for (const childReference of childReferences) {
                        const childId = referenceId(childReference)
                        if (childId) {
                            pendingIds.add(childId)
                        }
                    }
                }
            }
            for (const childId of Object.values(procurement.liveReconciliationRoundProcurementIdForCurrentCompany ?? {})) {
                pendingIds.add(childId)
            }
        }
        if (generation !== this._loadGeneration || !isLoadCurrent()) {
            return NO
        }
        this.rootInformation = rootInformation
        this.informationByProcurementId.clear()
        informationById.forEach((information, procurementId) => {
            this.informationByProcurementId.set(procurementId, information)
        })
        this.roundsByPartId.clear()
        for (const part of rootInformation.procurement.parts) {
            const rootPartId = part._id.toString()
            const rounds: ProcurementRound[] = [{
                procurementId: rootId,
                partId: rootPartId,
                rootPartId,
                roundNumber: 0,
                information: rootInformation
            }]
            for (const [procurementId, information] of informationById) {
                if (procurementId === rootId) {
                    continue
                }
                const procurement = information.procurement
                if (procurement.parts.length !== 1) {
                    continue
                }
                let ancestorId: string = procurementId
                let roundNumber = 0
                let sourceRootPartId = ""
                const ancestors = new Set<string>()
                while (ancestorId !== rootId && !ancestors.has(ancestorId)) {
                    ancestors.add(ancestorId)
                    const ancestor = informationById.get(ancestorId)?.procurement
                    const parentId = referenceId(ancestor?.reconciliationParentProcurement)
                    if (!parentId) {
                        break
                    }
                    if (parentId === rootId) {
                        sourceRootPartId = ancestor?.reconciliationParentPartId ?? ""
                    }
                    ancestorId = parentId
                    roundNumber += 1
                }
                if (ancestorId !== rootId) {
                    continue
                }
                if (sourceRootPartId !== rootPartId) {
                    continue
                }
                rounds.push({
                    procurementId,
                    partId: procurement.parts[0]._id.toString(),
                    rootPartId,
                    roundNumber,
                    information
                })
            }
            rounds.sort((firstRound, secondRound) => firstRound.roundNumber - secondRound.roundNumber)
            this.roundsByPartId.set(rootPartId, rounds)
        }
        const roundProcurementIds = new Set([...this.roundsByPartId.values()].flat().map(round => round.procurementId))
        for (const procurementId of this.informationByProcurementId.keys()) {
            if (!roundProcurementIds.has(procurementId)) {
                this.informationByProcurementId.delete(procurementId)
            }
        }
        return YES
    }

    selectedRoundForPart(partId: string): ProcurementRound | undefined {
        const rounds = this.roundsByPartId.get(partId)
        if (!rounds?.length) {
            return undefined
        }
        const selectedId = this.selectedProcurementIdsByPartId.get(partId)
        return rounds.find(round => round.procurementId === selectedId) ?? rounds[rounds.length - 1]
    }

    selectRoundForPart(procurementId: string, partId: string): boolean {
        if (!this.roundsByPartId.get(partId)?.some(round => round.procurementId === procurementId)) {
            return NO
        }
        this.selectedProcurementIdsByPartId.set(partId, procurementId)
        this.focusedPartId = partId
        return YES
    }

    isHistoricalRound(round: ProcurementRound): boolean {
        const procurement = round.information.procurement
        return this.hasNextRound(round) || procurement.status !== "published" ||
            round.information.isClosedWithoutContractInReconciliationChain === YES ||
            (procurement.finalizedPartIds ?? []).includes(round.partId) ||
            (procurement.reconciliationLockedPartIds ?? []).includes(round.partId)
    }

    hasNextRound(round: ProcurementRound): boolean {
        return this.roundsByPartId.get(round.rootPartId)?.some(candidate =>
            candidate.roundNumber > round.roundNumber
        ) === YES
    }

    static titleForRound(round: Pick<ProcurementRound, "roundNumber">): string {
        return `${round.roundNumber + 1}. voor`
    }
}
